import { useRef, useState, type ReactNode } from 'react';

import { Avatar, Box, Card, Stack, TextField, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import ModalScreen from '@/designs-dev/device/ModalScreen';
import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AppBar from '@/karehero-app/shell/AppBar';
import BackChevron from '@/karehero-app/shell/BackChevron';
import CareCircle from '@/karehero-app/onboarding/CareCircle';
import { RECIPIENT_NAME } from '@/karehero-app/onboarding/Onboarding.content';
import {
  CARED_FOR,
  CIRCLE_SWITCHER,
} from '@/karehero-app/shell/CircleSwitcher.content';

/** The drawer, or one of the pages it opens. */
export type CircleView = 'list' | 'manage' | 'add';

// A row under the people: something you do to a circle rather than a circle.
export const MenuRow = ({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) => (
  <Card variant='option' onClick={onClick}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
      <Box sx={{ color: 'primary.main' }}>
        <Icon icon={icon as never} fontSize={16} />
      </Box>
      <Typography variant='h6'>{label}</Typography>
    </Stack>
  </Card>
);

// What the name in the app bar opens: which of the people you care for the app
// is currently about, the circle around whichever one that is, and the way to
// start another.
//
// All of it on the name, none of it in settings. The bar names one person and
// every screen under it is theirs, so the name is the honest place for anything
// that changes which circle you are in or who is in it; splitting it left half
// of the subject in the account menu and half on the header.
//
// A drawer for the list and a page for the two jobs. Picking somebody is one
// tap on a short list, which is what a drawer is for; managing a circle and
// starting one are neither.
const CircleSwitcher = ({
  current,
  onSwitch,
  initialView = 'list',
  initialInviteOpen = false,
  onClose,
}: {
  /** The name the bar is showing, so the list can tick it. */
  current?: string;
  onSwitch: (name: string) => void;
  /** Harness-only: open straight onto one of the pages, for a flows page. */
  initialView?: CircleView;
  /** Harness-only: and that circle with its invite sheet up. */
  initialInviteOpen?: boolean;
  onClose: () => void;
}) => {
  const [view, setView] = useState<CircleView>(initialView);
  const [name, setName] = useState('');
  // The drawer slides down first and the page comes up after it, rather than
  // being unmounted under a page it outranks. A ref, because the drawer calls
  // the onClose it was handed when the row was tapped, a quarter of a second
  // later.
  const going = useRef<CircleView | null>(null);
  const leaveFor = (next: CircleView, close: () => void) => {
    going.current = next;
    close();
  };

  const page = (title: string, body: ReactNode) => (
    <ModalScreen from='right' onClose={onClose}>
      {(close) => (
        <ScreenRoot>
          <StatusBar />
          <AppBar
            title={title}
            size='small'
            leading={<BackChevron label='Back' onClick={close} />}
          />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, py: 2 }}>
            {body}
          </Box>
        </ScreenRoot>
      )}
    </ModalScreen>
  );

  // Who is in this circle and what they can see.
  if (view === 'manage')
    return page(
      `${RECIPIENT_NAME}’s care circle`,
      <CareCircle initialInviteOpen={initialInviteOpen} />,
    );

  // Onboarding's own three fields. Starting a circle is that screen again, for
  // the second person.
  if (view === 'add')
    return page(
      CIRCLE_SWITCHER.add,
      <Stack spacing={2}>
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Their name'
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Date of birth'
          fullWidth
        />
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='Relationship to you'
          fullWidth
        />
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel='Start their care circle'
          isDisabled={!name.trim()}
          onPress={() => {
            onSwitch(name.trim());
            onClose();
          }}
        >
          Start their care circle
        </Button>
      </Stack>,
    );

  return (
    <BottomSheet
      title={CIRCLE_SWITCHER.title}
      variant='hug'
      onClose={() => (going.current ? setView(going.current) : onClose())}
    >
      {(close) => (
        <Stack sx={{ pb: 2 }} spacing={1}>
          {CARED_FOR.map((person) => {
            const on = person.name === current;
            return (
              <Card
                key={person.name}
                variant='option'
                onClick={() => {
                  onSwitch(person.name);
                  close();
                }}
                sx={on ? { borderColor: 'primary.main' } : undefined}
              >
                <Stack
                  sx={{ alignItems: 'center' }}
                  direction='row'
                  spacing={1.5}
                >
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: 'custom.limeGreen.500',
                      color: 'primary.main',
                      fontSize: 14,
                    }}
                  >
                    {person.initials}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='h6'>{person.name}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {person.relationship}
                    </Typography>
                  </Box>
                  {/* A tick rather than a radio: you are not setting a
                      preference, you are being told where you are. */}
                  {on && (
                    <Box sx={{ color: 'primary.main' }}>
                      <Icon icon={'check' as never} fontSize={16} />
                    </Box>
                  )}
                </Stack>
              </Card>
            );
          })}

          {/* The two things you do to circles rather than with them, under the
              people they are about. */}
          <MenuRow
            icon='user-group'
            label={CIRCLE_SWITCHER.manage}
            onClick={() => leaveFor('manage', close)}
          />
          <MenuRow
            icon='user-plus'
            label={CIRCLE_SWITCHER.add}
            onClick={() => leaveFor('add', close)}
          />
        </Stack>
      )}
    </BottomSheet>
  );
};

CircleSwitcher.displayName = 'CircleSwitcher';
export default CircleSwitcher;
